import React, { useState, useEffect } from 'react';
import { ChevronRight, AlertCircle, FileText, Award, Eye, X, ExternalLink, Info, Workflow } from 'lucide-react';
import { Link } from 'react-router-dom';
import projectService from '../../services/projectService';
import { supabase } from '../../lib/supabase';
import logger from '../../utils/logger';
import { useAuth } from '../../AuthProvider';
import { Spinner } from '../ui';
import SetWorkflowModal from '../modals/SetWorkflowModal';

// Import existing sub-components
import YoutubeLinkCard from './detail/YoutubeLinkCard';
import OverviewCard from './detail/OverviewCard';
import DescriptionCard from './detail/DescriptionCard';
import LeaveNoteCard from './detail/LeaveNoteCard';
import TranscriptionCard from './detail/TranscriptionCard';
import EntitiesCard from './detail/EntitiesCard';
import PromptCard from './detail/PromptCard';
import IABTaxonomyCard from './detail/IABTaxonomyCard';

// Import new sub-components
import ComparisonAnalysisCard from './detail/ComparisonAnalysisCard';
import MetricsCard from './detail/MetricsCard';
import OfficialMetricsCard from './detail/OfficialMetricsCard';

const SynopsisComparisonProjectDetailPage = ({ project: initialProject, details, onClose, onDelete, onRefresh }) => {
  const { user } = useAuth();
  const canDeleteProject = user?.role === 'admin' || user?.app_metadata?.role === 'admin';
  const workflowStatus = initialProject?.workflowStatus ?? initialProject?.workflow_status ?? details?.project?.workflow_status;
  const isDelivered = workflowStatus === 'delivered';
  const canSetWorkflow = user?.role === 'admin' || user?.role === 'pm' || user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'pm';
  const [project, setProject] = useState(initialProject);
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [officialEvaluation, setOfficialEvaluation] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(true);

  // Prompt Modal State
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [promptContent, setPromptContent] = useState('');
  const [userPromptContent, setUserPromptContent] = useState(''); // Added for user prompt
  const [isLoadingPrompt, setIsLoadingPrompt] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [modalTitle, setModalTitle] = useState('Comparison Evaluation Prompt'); // Added for dynamic title

  const handleCheckPrompt = async (type = 'default') => {
    setShowPromptModal(true);
    setIsLoadingPrompt(true);
    setPromptContent('');
    setUserPromptContent('');
    
    try {
      if (type === 'official') {
        setModalTitle('Official Story Evaluation Prompt');
        const [sysResult, userResult] = await Promise.all([
          projectService.getAdminConstant('OFFICIAL_EVALUATION_SYSTEM_PROMPT'),
          projectService.getAdminConstant('OFFICIAL_EVALUATION_USER_PROMPT')
        ]);
        setPromptContent(sysResult?.value || 'Failed to load official system prompt.');
        setUserPromptContent(userResult?.value || 'Failed to load official user prompt.');
      } else {
        setModalTitle('Comparison Evaluation Prompt');
        const result = await projectService.getAdminConstant('COMPARISON_EVALUATION_PROMPT');
        setPromptContent(result?.value || 'Failed to load comparison prompt.');
      }

      // Check if user is admin
      const { data: { session } } = await supabase.auth.getSession();
      const userRole = session?.user?.app_metadata?.role || session?.user?.role;
      setIsAdmin(userRole === 'admin');
    } catch (error) {
      console.error('Error fetching prompt:', error);
      setPromptContent('Error loading prompt.');
    } finally {
      setIsLoadingPrompt(false);
    }
  };

  const metadata = details?.metadata || project?.metadata;
  const synopsis = details?.synopsis || project?.synopsis;
  const transcript = details?.transcript || project?.transcript;
  const referenceSynopsis = project?.comparison_synopsis_text;

  useEffect(() => {
    const fetchComparison = async () => {
      if (!initialProject?.id) return;
      
      try {
        setLoadingComparison(true);
        // Fetch both evaluation datasets in parallel
        const [comparisonResult, officialResult] = await Promise.all([
          projectService.getSynopsisComparison(initialProject.id),
          projectService.getOfficialEvaluation(initialProject.id)
        ]);
        
        if (comparisonResult.project) {
          setProject(prev => ({ ...prev, ...comparisonResult.project }));
        }
        
        setComparisonData(comparisonResult.comparison);
        setOfficialEvaluation(officialResult.evaluation);
        
        logger.log('✅ Evaluation data loaded:', {
          comparison: comparisonResult.comparison,
          official: officialResult.evaluation
        });
      } catch (err) {
        logger.error('❌ Error loading evaluation:', err);
      } finally {
        setLoadingComparison(false);
      }
    };

    fetchComparison();
  }, [initialProject?.id, synopsis?.id]); // Re-fetch if synopsis changes (e.g. regeneration)

  const handleRegenerateComplete = async () => {
    if (onRefresh) {
      await onRefresh();
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-neutral-950">
      {/* Header */}
      <div className="bg-white dark:bg-neutral-950 border-b border-gray-200 dark:border-neutral-800 shadow-sm flex-shrink-0 h-15">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                title="Back to projects"
              >
                <ChevronRight className="w-6 h-6 text-gray-600 dark:text-neutral-400 rotate-180" />
              </button>
              <div>
                <div className="flex items-center space-x-2">
                  <h1 className="text-xl font-semibold text-gray-900 dark:text-neutral-100">
                    {project?.title || project?.name || 'Comparison Analysis'}
                  </h1>
                  <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded text-[10px] font-bold uppercase tracking-wider border border-purple-200 dark:border-purple-800">
                    Comparison
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Workflow: {project?.workflowStatusDisplayName ?? project?.workflow_status_display_name ?? details?.project?.workflow_status_display_name ?? '—'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {canSetWorkflow && !isDelivered && (
                <button
                  type="button"
                  onClick={() => setWorkflowModalOpen(true)}
                  className="border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 hover:bg-gray-100 dark:hover:bg-neutral-800 text-gray-700 dark:text-neutral-200 px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2"
                >
                  <Workflow className="w-4 h-4" /> Set Workflow
                </button>
              )}
              {canDeleteProject && (
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this project?')) {
                      onDelete(project.id);
                    }
                  }}
                  className="bg-gray-200 dark:bg-neutral-800 hover:bg-gray-300 dark:hover:bg-neutral-700 text-gray-700 dark:text-neutral-200 border border-gray-300 dark:border-neutral-700 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  DELETE
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <SetWorkflowModal
        isOpen={workflowModalOpen}
        onClose={() => setWorkflowModalOpen(false)}
        projectId={project?.id}
        projectType={project?.projectType ?? project?.project_type ?? 'synopsis_comparison'}
        isLocked={isDelivered}
        onSaved={onRefresh}
      />
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 pb-8">
          {loadingComparison && !comparisonData ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Spinner size="xl" className="mb-4 text-primary" />
              <p className="text-gray-500">Loading comparison analysis...</p>
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-4">
              {/* Left Column - Info */}
              <div className="col-span-3 space-y-3">
                <YoutubeLinkCard metadata={metadata} />
                <OverviewCard 
                  project={project} 
                  metadata={metadata} 
                  transcript={transcript}
                  currentSynopsis={synopsis}
                />
                <DescriptionCard metadata={metadata} />
                <LeaveNoteCard projectId={project?.id} initialNote={project?.notes || details?.notes || ''} />
              </div>
              
              {/* Right Side - Analysis & Results (9 columns) */}
              <div className="col-span-9 space-y-4">
                {!comparisonData && !loadingComparison ? (
                  <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 p-6 rounded-xl flex items-start space-x-4">
                    <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-amber-800 dark:text-amber-400 font-semibold">No comparison data yet</h3>
                      <p className="text-amber-700/70 dark:text-amber-400/70 text-sm mt-1">
                        The comparison analysis will be generated automatically once the AI synopsis is ready. 
                        If it's already generated, try refreshing the page.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Row 1: Side-by-side View */}
                    <ComparisonAnalysisCard 
                      referenceSynopsis={referenceSynopsis}
                      generatedSynopsis={synopsis?.synopsis_text}
                      analysisText={null} // Analysis is now handled below
                    />

                    {/* Row 2: Official Evaluation Metrics */}
                    {officialEvaluation && (
                      <div className="bg-white dark:bg-neutral-950 rounded-lg border border-gray-200 dark:border-neutral-800 overflow-hidden shadow-sm">
                        <div className="p-4 border-b border-gray-200 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-900/50 flex justify-between items-center">
                          <h2 className="text-lg font-semibold text-gray-900 dark:text-neutral-100 flex items-center">
                            <FileText className="w-5 h-5 mr-2 text-sky-600" />
                            Official Evaluation
                          </h2>
                          <button
                            onClick={() => handleCheckPrompt('official')}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-md text-xs font-medium text-gray-600 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors shadow-sm"
                          >
                            <Eye className="w-3.5 h-3.5 text-sky-500" />
                            <span>Check Prompt</span>
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200 dark:divide-neutral-800">
                          {/* Left: Official Reasoning (2/3) */}
                          <div className="col-span-2 p-4 flex flex-col">
                            <div className="flex items-center space-x-2 mb-4">
                              <Award className="w-5 h-5 text-indigo-600" />
                              <h3 className="text-sm font-semibold tracking-wider text-gray-500 dark:text-neutral-400 uppercase">Expert Story Editor Analysis</h3>
                            </div>
                            <div className="bg-indigo-50/20 dark:bg-indigo-900/5 rounded-xl p-6 border border-indigo-100 dark:border-indigo-900/30 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-neutral-700 min-h-[300px] max-h-[780px]">
                              <p className="text-sm text-gray-700 dark:text-neutral-300 whitespace-pre-wrap leading-relaxed">
                                {officialEvaluation.reasoning || 'No expert reasoning available.'}
                              </p>
                            </div>
                          </div>
                          
                          {/* Right: Official Metrics (1/3) */}
                          <div className="col-span-1 p-4 space-y-6 bg-indigo-50/10 dark:bg-indigo-900/5">
                            <OfficialMetricsCard evaluation={officialEvaluation} noBorder hideReasoning />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Row 3: Standard Evaluation Metrics */}
                    <div className="bg-white dark:bg-neutral-950 rounded-lg border border-gray-200 dark:border-neutral-800 overflow-hidden shadow-sm">
                      <div className="p-4 border-b border-gray-200 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-900/50 flex justify-between items-center">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-neutral-100 flex items-center">
                          <FileText className="w-5 h-5 mr-2 text-sky-600" />
                          LLM Default Evaluation
                        </h2>
                        <button
                          onClick={handleCheckPrompt}
                          className="flex items-center space-x-1 px-3 py-1.5 bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-md text-xs font-medium text-gray-600 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors shadow-sm"
                        >
                          <Eye className="w-3.5 h-3.5 text-sky-500" />
                          <span>Check Prompt</span>
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200 dark:divide-neutral-800">
                        {/* Left: Detailed LLM Analysis (2/3) */}
                        <div className="col-span-2 p-4 flex flex-col">
                          <div className="flex items-center space-x-2 mb-4">
                            <FileText className="w-5 h-5 text-sky-600" />
                            <h3 className="text-sm font-semibold tracking-wider text-gray-500 dark:text-neutral-400 uppercase">Detailed Comparison Insight</h3>
                          </div>
                          <div className="bg-gray-50 dark:bg-neutral-900 rounded-xl p-6 border border-gray-200 dark:border-neutral-800 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-neutral-700 min-h-[300px] max-h-[530px]">
                            <p className="text-sm text-gray-700 dark:text-neutral-300 whitespace-pre-wrap leading-relaxed">
                              {comparisonData?.analysis_text || 'No detailed analysis available.'}
                            </p>
                          </div>
                        </div>
                        
                        {/* Right: Standard Metrics (1/3) */}
                        <div className="col-span-1 p-4 space-y-6 bg-gray-50/30 dark:bg-neutral-900/30">
                          <MetricsCard metrics={comparisonData?.metrics} noBorder />
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                {/* Row 4: Additional Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <IABTaxonomyCard iabTaxonomy={synopsis?.iab_taxonomy} />
                    <PromptCard 
                      currentSynopsis={synopsis}
                      details={details}
                      projectId={project?.id}
                      onRegenerateComplete={handleRegenerateComplete}
                    />
                  </div>
                  <div className="space-y-4">
                    <TranscriptionCard transcript={transcript} />
                    <EntitiesCard entities={synopsis?.entities} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Prompt Modal */}
      {showPromptModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200 dark:border-neutral-800 flex justify-between items-center bg-gray-50/50 dark:bg-neutral-900/50">
              <h3 className="text-md font-bold text-gray-900 dark:text-neutral-100 flex items-center">
                <FileText className="w-4 h-4 mr-2 text-sky-500" />
                {modalTitle}
              </h3>
              <button
                onClick={() => setShowPromptModal(false)}
                className="p-1 hover:bg-gray-200 dark:hover:bg-neutral-800 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-neutral-400" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {isLoadingPrompt ? (
                <div className="flex flex-col items-center justify-center py-12 space-y-4">
                  <Spinner size="lg" className="text-primary" />
                  <p className="text-sm text-gray-500 dark:text-neutral-400">Loading prompt template...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">System Prompt</h4>
                    <div className="bg-gray-50 dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-xl p-4">
                      <pre className="text-xs text-gray-800 dark:text-neutral-300 whitespace-pre-wrap leading-relaxed font-mono">
                        {promptContent}
                      </pre>
                    </div>
                  </div>

                  {userPromptContent && (
                    <div className="space-y-2">
                      <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">User Prompt Template</h4>
                      <div className="bg-gray-50 dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-xl p-4">
                        <pre className="text-xs text-gray-800 dark:text-neutral-300 whitespace-pre-wrap leading-relaxed font-mono">
                          {userPromptContent}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Token Reference */}
                  <div className="bg-sky-50 dark:bg-sky-900/10 border border-sky-100 dark:border-sky-800/30 rounded-xl p-4">
                    <h4 className="text-[11px] font-bold text-sky-800 dark:text-sky-300 mb-2 flex items-center uppercase tracking-wider">
                      <Info className="w-3.5 h-3.5 mr-1.5" />
                      Prompt Token Reference
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="text-[10px] text-sky-700 dark:text-sky-400">
                        <code className="bg-sky-100 dark:bg-sky-900/40 px-1 py-0.5 rounded font-bold text-sky-900 dark:text-sky-200">{'{reference_text}'}</code>
                        <p className="mt-1 leading-tight">The human-verified reference text used as the standard for comparison.</p>
                      </div>
                      <div className="text-[10px] text-sky-700 dark:text-sky-400">
                        <code className="bg-sky-100 dark:bg-sky-900/40 px-1 py-0.5 rounded font-bold text-sky-900 dark:text-sky-200">{'{generated_text}'}</code>
                        <p className="mt-1 leading-tight">The AI-generated synopsis or output that needs to be evaluated.</p>
                      </div>
                      <div className="text-[10px] text-sky-700 dark:text-sky-400">
                        <code className="bg-sky-100 dark:bg-sky-900/40 px-1 py-0.5 rounded font-bold text-sky-900 dark:text-sky-200">{'{context}'}</code>
                        <p className="mt-1 leading-tight">Metadata like video title, description, or transcript for better analysis.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col space-y-3 pt-2">
                    <div className="flex items-start space-x-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900/30 rounded-lg">
                      <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                      <p className="text-[11px] text-amber-700 dark:text-amber-300 leading-normal">
                        Only Admin users can edit the comparison prompt. This prompt is used to evaluate AI-generated synopses against human reference synopses.
                      </p>
                    </div>

                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="flex items-center self-end space-x-1.5 text-xs font-semibold text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 transition-colors py-1 px-2 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded"
                      >
                        <span>Go to Admin Page</span>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-neutral-800 flex justify-end">
              <button
                onClick={() => setShowPromptModal(false)}
                className="px-4 py-2 bg-gray-100 dark:bg-neutral-800 hover:bg-gray-200 dark:hover:bg-neutral-700 text-gray-700 dark:text-neutral-300 rounded-lg text-sm font-semibold transition-colors shadow-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SynopsisComparisonProjectDetailPage;

